/**
 * Hook for fetching KPI Detail data (used by KPITrendModal).
 * Extracted from KPITrendModal.tsx for better code organization.
 */

import { useCallback, useEffect, useState } from 'react';
import { getKpiDetail, Granularity, KpiDetailResponse } from '../services/kpiDetailApi';
import { getDisplayName } from '../utils/kpiHelpers';

export interface KpiMetricForModal {
	name: string;
	dbName?: string;
	category: string;
	actual: number | null;
	target: number | null;
	delta: number | null;
	isUnfavorable: boolean | null;
	format: import('../utils/formatters').KpiFormat;
}

interface UseKpiDetailParams {
	visible: boolean;
	metric: KpiMetricForModal | null;
	locationToken: string;
	yearToken: string;
	periodToken: string;
	weekToken: string;
}

interface UseKpiDetailReturn {
	loading: boolean;
	error: string | null;
	detail: KpiDetailResponse | null;
	granularity: Granularity;
	setGranularity: (g: Granularity) => void;
	isDropdownOpen: boolean;
	setIsDropdownOpen: (open: boolean) => void;
	handleGranularitySelect: (value: Granularity) => void;
	fetchDetail: (gran: Granularity) => Promise<void>;
}

export function useKpiDetail({
	visible,
	metric,
	locationToken,
	yearToken,
	periodToken,
	weekToken,
}: UseKpiDetailParams): UseKpiDetailReturn {
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [detail, setDetail] = useState<KpiDetailResponse | null>(null);
	const [granularity, setGranularity] = useState<Granularity>('period');
	const [isDropdownOpen, setIsDropdownOpen] = useState(false);

	// Reset when modal closes
	useEffect(() => {
		if (!visible) {
			setIsDropdownOpen(false);
			setGranularity('period');
			setError(null);
		}
	}, [visible]);

	const fetchDetail = useCallback(async (gran: Granularity) => {
		if (!metric || !visible) { return; }
		setLoading(true);
		setError(null);
		try {
			const data = await getKpiDetail({
				kpi:         metric.dbName || metric.name,
				location:    locationToken,
				year:        yearToken,
				period:      periodToken || null,
				week:        weekToken   || null,
				granularity: gran,
			});
			setDetail({ ...data, name: getDisplayName(data.name) });
		} catch (err: any) {
			setError(err?.message || 'Failed to load KPI detail.');
		} finally {
			setLoading(false);
		}
	}, [metric, visible, locationToken, yearToken, periodToken, weekToken]);

	// Refetch whenever modal opens, metric changes, granularity changes, or filters change
	useEffect(() => {
		if (visible && metric) {
			fetchDetail(granularity);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [visible, metric, granularity, locationToken, yearToken, periodToken, weekToken]);

	const handleGranularitySelect = (value: Granularity) => {
		setGranularity(value);
		setIsDropdownOpen(false);
	};

	return {
		loading,
		error,
		detail,
		granularity,
		setGranularity,
		isDropdownOpen,
		setIsDropdownOpen,
		handleGranularitySelect,
		fetchDetail,
	};
}
